import { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { recordExploreClick } from "@/lib/exploreHistory";
import { track } from "@/lib/analytics";
import type { ExploreResultItem } from "@/types/exploreResult";

/**
 * Shared card click handler for Explore (mobile + desktop).
 * - Records click in localStorage history (for "Utvalt för dig")
 * - Tracks explore_card_clicked analytics
 * - Navigates to item href (List = Go)
 */
export function useExploreCardClick() {
  const navigate = useNavigate();

  const handleCardClick = useCallback(
    (item: ExploreResultItem, sectionKey: string, positionIndex: number) => {
      // Record for personalization (non-blocking)
      try {
        recordExploreClick(item.id, item.badges);
      } catch {
        // fail-safe
      }

      // Analytics (fail-safe)
      try {
        track(
          "explore_card_clicked",
          {
            type: item.kind,
            id: item.id,
            position_index: positionIndex,
            section_name: sectionKey,
          },
          "explore"
        );
      } catch {
        // fail-safe
      }

      // Navigate (List = Go)
      navigate(item.href);
    },
    [navigate]
  );

  return handleCardClick;
}

/**
 * Throttled map move tracker.
 * Emits explore_map_moved at most once per interval.
 */
export function useThrottledMapTrack(intervalMs = 3000) {
  const lastRef = useRef(0);

  return useCallback(
    (centerLat: number, centerLng: number) => {
      const now = Date.now();
      if (now - lastRef.current < intervalMs) return;
      lastRef.current = now;

      try {
        track(
          "explore_map_moved",
          {
            bounds_center_lat: Math.round(centerLat * 1000) / 1000,
            bounds_center_lng: Math.round(centerLng * 1000) / 1000,
          },
          "explore"
        );
      } catch {
        // fail-safe
      }
    },
    [intervalMs]
  );
}
